/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\CdrGeneralSettingsBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the CDR Settings module.
----------------------------------------------------------------*/


import { Database } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { Label } from '../../../components/ui/label';
import { useI18n } from '../../../hooks/useI18n';
import type { CdrSettingsData, LegsOption, SpoolFormatOption } from '../types';
const SELECT_CLS = 'w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary';

interface Props {
    settingsData: CdrSettingsData;
    isLoading: boolean;
    onChange: <K extends keyof CdrSettingsData>(field: K, value: CdrSettingsData[K]) => void;
}

function Row({ id, tooltipKey, labelKey, children }: { id?: string; tooltipKey: string; labelKey: string; children: React.ReactNode }) {
    const { t } = useI18n();
    return (
        <>
            <SimpleTooltip content={t(tooltipKey)} className="col-span-2">
                <Label htmlFor={id} className="text-left">{t(labelKey)}</Label>
            </SimpleTooltip>
            <div className="col-span-2">{children}</div>
        </>
    );
}

export function CdrGeneralSettingsBox({ settingsData, isLoading, onChange }: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('cdrSettings.sections.general')} icon={<Database className="h-5 w-5" />} isLoading={isLoading}>
            <div className="grid grid-cols-8 items-start gap-x-6 gap-y-4">
                <Row id="legs" tooltipKey="cdrSettings.fields.legs.tooltip" labelKey="cdrSettings.fields.legs.label">
                    <select id="legs" value={settingsData.legs} onChange={(e) => onChange('legs', e.target.value as LegsOption)} className={SELECT_CLS}>
                        <option value="a">{t('cdrSettings.options.legs.a')}</option>
                        <option value="b">{t('cdrSettings.options.legs.b')}</option>
                        <option value="ab">{t('cdrSettings.options.legs.ab')}</option>
                    </select>
                </Row>

                <Row id="spoolFormat" tooltipKey="cdrSettings.fields.spoolFormat.tooltip" labelKey="cdrSettings.fields.spoolFormat.label">
                    <select id="spoolFormat" value={settingsData.spoolFormat} onChange={(e) => onChange('spoolFormat', e.target.value as SpoolFormatOption)} className={SELECT_CLS}>
                        <option value="csv">{t('cdrSettings.options.spoolFormat.csv')}</option>
                        <option value="sql">{t('cdrSettings.options.spoolFormat.sql')}</option>
                    </select>
                </Row>

                <Row id="rotateOnHup" tooltipKey="cdrSettings.fields.rotateOnHup.tooltip" labelKey="cdrSettings.fields.rotateOnHup.label">
                    <div className="flex items-center"><Toggle id="rotateOnHup" value={settingsData.rotateOnHup} onChange={(v) => onChange('rotateOnHup', v)} /></div>
                </Row>

                <Row id="debug" tooltipKey="cdrSettings.fields.debug.tooltip" labelKey="cdrSettings.fields.debug.label">
                    <div className="flex items-center"><Toggle id="debug" value={settingsData.debug} onChange={(v) => onChange('debug', v)} /></div>
                </Row>
            </div>
        </FormBox>
    );
}
